/**
 * Scan details: overall outcome plus each engine's result and error.
 */

import { useQuery } from '@tanstack/react-query';
import StatusBadge from '@/components/common/StatusBadge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { getScanEngines } from '@/api/scans';
import { completedWithErrors, formatDate } from '@/utils/formatters';
import { apiError } from '@/utils/apiError';
import type { Scan, ScanEngineOutcome } from '@/types/scan';

const engineStatus: Record<ScanEngineOutcome['status'], string> = {
  completed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped',
};

export default function ScanDetails({ scan }: { scan: Scan }) {
  const engines = useQuery({ queryKey: ['scan-engines', scan.id], queryFn: () => getScanEngines(scan.id) });
  const partial = completedWithErrors(scan);
  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={scan.status} withErrors={partial} />
        <span className="text-gray-500">Started {formatDate(scan.started_at ?? scan.created_at)}</span>
        {scan.completed_at && <span className="text-gray-500">Finished {formatDate(scan.completed_at)}</span>}
      </div>
      {scan.error_message && (
        <p role="alert" className={`rounded p-3 ${partial ? 'bg-amber-50 text-amber-900' : 'bg-red-50 text-red-700'}`}>{scan.error_message}</p>
      )}
      {partial && (
        <p className="text-gray-600">
          Results from the engines that completed are kept. Failed engines did not run their checks, so missing findings from them are not evidence that those checks passed.
          Within your plan&apos;s rescan gap, failed engines get one corrected retry on this target; engines that completed must wait for the gap to end.
        </p>
      )}
      {engines.isLoading && <LoadingSpinner label="Loading engine results..." />}
      {engines.error && <p role="alert" className="rounded bg-red-50 p-3 text-red-700">{apiError(engines.error)}</p>}
      {engines.data && engines.data.engines.length === 0 && <p className="text-gray-500">No engine results have been reported yet.</p>}
      {engines.data && engines.data.engines.length > 0 && (
        <div className="overflow-x-auto">
          <table aria-label="Engine results" className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="text-left text-xs font-medium uppercase text-gray-500">
                <th className="px-3 py-2">Target</th><th className="px-3 py-2">Engine</th><th className="px-3 py-2">Result</th><th className="px-3 py-2">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {engines.data.engines.map((row) => (
                <tr key={row.asset_id + row.engine}>
                  <td className="px-3 py-2">{row.asset_value ?? 'Removed asset'}</td>
                  <td className="px-3 py-2 font-mono text-xs">{row.engine}</td>
                  <td className={`px-3 py-2 ${row.status === 'failed' ? 'font-semibold text-red-700' : ''}`}>{engineStatus[row.status]}</td>
                  <td className="px-3 py-2 break-words text-gray-700">{row.error ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
